import { useEffect, useState } from "react";
import { Paperclip, X, RotateCcw } from "lucide-react";
import type { Template } from "@/lib/presets";
import * as UiDialog from "@/components/ui/dialog";
import { useApi } from "@/lib/api";
import { DictateButton } from "@/components/DictateButton";
import { t } from "@/i18n";

// dialog.jsx is untyped (forwardRef<any>), so give the parts loose props for TSX use
const { Dialog, DialogContent, DialogHeader, DialogTitle } = UiDialog as Record<string, React.ComponentType<any>>;

export type Resubmit = { id: string; reason: string; name: string; details: string; kept: number };

type Props = { tpl: Template; open: boolean; onOpenChange: (o: boolean) => void; label: string; busy: boolean; initialName?: string;
  resubmit?: Resubmit | null;
  onSubmit: (files: File[], note: string, title: string, extra?: Record<string, string>) => Promise<boolean> };

export function DrinkTemplate({ tpl, open, onOpenChange, label, busy, onSubmit, initialName = "", resubmit = null }: Props) {
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const api = useApi();
  const [files, setFiles] = useState<File[]>([]);
  const ready = name.trim() && (details.trim() || files.length || resubmit?.kept);
  useEffect(() => { if (open && initialName && !resubmit) setName((n) => n || initialName); }, [open, initialName, resubmit]);
  useEffect(() => {
    if (open && resubmit) { setName(resubmit.name); setDetails(resubmit.details); setFiles([]); }
  }, [open, resubmit]);

  const submit = async () => {
    const note = `${tpl.prefix}: ${name.trim()}\n\n${details.trim() || "(see attachments)"}`;
    if (await onSubmit(files, note, name.trim(), { kind: tpl.kind, ...(resubmit ? { resubmit_of: resubmit.id } : {}) })) {
      setName(""); setDetails(""); setFiles([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-surface)] text-[var(--text-primary)] max-w-lg w-[calc(100vw-1.5rem)] rounded-2xl" data-testid="drink-template-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{resubmit ? <RotateCcw size={18} className="text-[var(--accent-primary)]" /> : <tpl.icon size={18} className="text-[var(--accent-primary)]" />} {t(resubmit ? "Fix & resubmit" : tpl.label)}</DialogTitle></DialogHeader>
        <div className="stack-sm">
          {resubmit && (
            <div className="rounded-xl bg-[var(--err-bg)] px-3 py-2" data-testid="resubmit-owner-note">
              <p className="text-xs font-semibold text-[var(--status-error)]">{t("Owner's note")}</p>
              <p className="text-sm">“{resubmit.reason || t("No reason given")}”</p>
            </div>
          )}
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder={t(tpl.namePh)} data-testid="drink-name-input" />
          <textarea className="field min-h-[200px]" value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t(tpl.detailsPh)} data-testid="drink-details-input" />
          <div className="flex items-center gap-2 text-xs faint"><DictateButton post={(fd: FormData) => api.post("/transcribe", fd)} onText={(s: string) => setDetails((p) => (p ? `${p}\n${s}` : s))} testid="drink-dictate-button" /> {t(tpl.dictateHint)}</div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="btn btn-ghost btn-sm cursor-pointer" data-testid="drink-attach-button">
              <Paperclip size={14} /> {t("Photo / video")}
              <input type="file" multiple hidden accept="image/*,video/*,audio/*" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="drink-file-input" />
            </label>
            {!!resubmit?.kept && <span className="chip chip-muted" data-testid="resubmit-kept-files">{t(resubmit.kept > 1 ? "Keeps {n} earlier files" : "Keeps {n} earlier file", { n: resubmit.kept })}</span>}
            {files.map((f, i) => (
              <span key={i} className="chip chip-muted">{f.name.slice(0, 18)}<button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={t("Remove")}><X size={11} /></button></span>
            ))}
          </div>
          <button className="btn btn-primary w-full !bg-[var(--status-ok)]" disabled={!ready || busy} onClick={submit} data-testid="drink-submit-button">{busy ? t("Sending…") : resubmit ? t("Resubmit for approval") : label}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
